// Danh mục tài liệu (dạng cây, tối đa 3 cấp): thêm/sửa/xóa, danh mục con, ẩn/hiện trên cây, bật/tắt sử dụng, kéo thả trong cùng cấp
import { useMemo, useState } from 'react';
import { Button, Drawer, Form, Input, Modal, Select, Switch, Table, Tooltip, TreeSelect, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Eye, EyeOff, Folder, FolderPlus, GripVertical, Info, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import DocShell from 'pages/Documents/components/DocShell';
import useDocsStore from 'pages/Documents/store';
import type { DocCategory } from 'pages/Documents/data';
import { buildCategoryTree, categoryDepth, descendantIds, matches, nextCategoryCode, type CategoryNode } from 'pages/Documents/logic';
import { useDocAuth } from 'pages/Documents/utils';

const MAX_DEPTH = 3;
type FormValues = Pick<DocCategory, 'code' | 'name' | 'typeId' | 'parentId' | 'active' | 'visible'>;

const HeaderHint = ({ label, hint }: { label: string; hint: string }) => (
  <span className="inline-flex items-center gap-1">{label}<Tooltip title={hint}><Info size={13} className="text-slate-400" /></Tooltip></span>
);

export default function DocumentCategories() {
  const { can } = useDocAuth();
  const editable = can('config');
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const docs = useDocsStore(s => s.docs);
  const saveCategory = useDocsStore(s => s.saveCategory);
  const deleteCategory = useDocsStore(s => s.deleteCategory);
  const reorderCategories = useDocsStore(s => s.reorderCategories);
  const [modal, ctx] = Modal.useModal();
  const [form] = Form.useForm<FormValues>();

  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive' | 'hidden'>('all');
  const [editing, setEditing] = useState<DocCategory | 'new' | null>(null);
  const [expanded, setExpanded] = useState<React.Key[] | null>(null);
  const [drag, setDrag] = useState<{ id: string; parentId: string | null; over?: string; pos?: 'top' | 'bottom' } | null>(null);
  const parentId = Form.useWatch('parentId', form);

  const typeName = (id: string) => types.find(t => t.id === id)?.name || '—';
  const docCount = useMemo(() => {
    const direct: Record<string, number> = {};
    docs.forEach(d => { if (!d.deletedAt) direct[d.categoryId] = (direct[d.categoryId] || 0) + 1; });
    const total: Record<string, { direct: number; all: number }> = {};
    categories.forEach(c => {
      const all = [c.id, ...descendantIds(categories, c.id)].reduce((s, id) => s + (direct[id] || 0), 0);
      total[c.id] = { direct: direct[c.id] || 0, all };
    });
    return total;
  }, [docs, categories]);
  const docsAnyState = (id: string) => docs.filter(d => d.categoryId === id).length;

  const filtering = !!q || !!typeFilter || status !== 'all';
  const tree = useMemo(() => buildCategoryTree(categories, filtering ? c =>
    matches(q, c.code, c.name) &&
    (!typeFilter || c.typeId === typeFilter) &&
    (status === 'all' || (status === 'active' ? c.active : status === 'inactive' ? !c.active : !c.visible)) : undefined),
  [categories, q, typeFilter, status, filtering]);
  const allIds = categories.map(c => c.id);
  const expandedKeys = filtering ? allIds : expanded ?? allIds;

  const openForm = (c: DocCategory | 'new', parent?: DocCategory) => {
    setEditing(c);
    if (c === 'new') {
      const pid = parent?.id ?? null;
      form.setFieldsValue({ parentId: pid, code: nextCategoryCode(categories, pid), name: '', typeId: parent?.typeId ?? types.find(t => t.active)?.id, active: true, visible: true });
    } else {
      form.setFieldsValue({ code: c.code, name: c.name, typeId: c.typeId, parentId: c.parentId, active: c.active, visible: c.visible });
    }
  };

  const editingId = editing && editing !== 'new' ? editing.id : null;
  const blocked = useMemo(() => new Set(editingId ? [editingId, ...descendantIds(categories, editingId)] : []), [categories, editingId]);
  const myHeight = useMemo(() => {
    const height = (id: string): number => {
      const kids = categories.filter(c => c.parentId === id);
      return kids.length ? 1 + Math.max(...kids.map(k => height(k.id))) : 0;
    };
    return editingId ? height(editingId) : 0;
  }, [categories, editingId]);
  const parentTree = useMemo(() => {
    const toNode = (n: CategoryNode): any => ({
      value: n.id, title: `${n.code} · ${n.name}`,
      disabled: blocked.has(n.id) || categoryDepth(categories, n.id) + 1 + myHeight > MAX_DEPTH,
      children: n.children?.map(toNode),
    });
    return buildCategoryTree(categories).map(toNode);
  }, [categories, blocked, myHeight]);
  const parent = categories.find(c => c.id === parentId);

  const onParentChange = (pid: string | null) => {
    const p = categories.find(c => c.id === pid);
    if (p) form.setFieldValue('typeId', p.typeId);
    if (editing === 'new') form.setFieldValue('code', nextCategoryCode(categories, pid ?? null));
  };

  const submit = async () => {
    const v = await form.validateFields().catch(() => null);
    if (!v) return;
    const values = { ...v, parentId: v.parentId ?? null, code: v.code.trim().toUpperCase(), name: v.name.trim() };
    saveCategory({ ...values, id: editingId ?? undefined });
    message.success(editingId ? 'Đã cập nhật danh mục' : 'Đã thêm danh mục');
    if (values.parentId) setExpanded(prev => Array.from(new Set([...(prev ?? allIds), values.parentId!])));
    setEditing(null);
  };

  const toggle = (c: DocCategory, field: 'active' | 'visible') => {
    const kids = descendantIds(categories, c.id).length;
    const apply = () => saveCategory({ ...c, [field]: !c[field] });
    if (field === 'active' && c.active && (kids || docCount[c.id].all)) {
      modal.confirm({
        title: `Ngừng sử dụng “${c.name}”?`,
        content: `Danh mục có ${kids} danh mục con và ${docCount[c.id].all} tài liệu. Sau khi ngừng, danh mục này và toàn bộ danh mục con không chọn được khi tạo tài liệu mới; tài liệu cũ giữ nguyên.`,
        okText: 'Ngừng sử dụng', cancelText: 'Hủy', onOk: apply,
      });
    } else apply();
  };

  const remove = (c: DocCategory) => {
    const kids = categories.filter(x => x.parentId === c.id).length;
    const used = docsAnyState(c.id);
    if (kids || used) {
      modal.warning({
        title: 'Không thể xóa danh mục',
        content: kids
          ? `“${c.name}” còn ${kids} danh mục con. Hãy xóa hoặc chuyển các danh mục con trước.`
          : `“${c.name}” đang có ${used} tài liệu (kể cả trong thùng rác). Hãy chuyển sang “Ngừng sử dụng” thay vì xóa.`,
        okText: 'Đã hiểu',
      });
      return;
    }
    modal.confirm({
      title: 'Xóa danh mục?', content: `Xóa “${c.code} · ${c.name}”. Hành động này không thể hoàn tác.`, okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy',
      onOk: () => { deleteCategory(c.id); message.success('Đã xóa danh mục'); },
    });
  };

  const drop = (target: DocCategory) => {
    if (!drag || drag.id === target.id || drag.parentId !== target.parentId) return setDrag(null);
    const ids = categories.filter(c => c.parentId === target.parentId).sort((a, b) => a.order - b.order).map(c => c.id).filter(id => id !== drag.id);
    ids.splice(ids.indexOf(target.id) + (drag.pos === 'bottom' ? 1 : 0), 0, drag.id);
    reorderCategories(ids);
    setDrag(null);
  };

  const inactiveAncestor = (c: DocCategory) => {
    const byId = new Map(categories.map(x => [x.id, x]));
    let cur = c.parentId ? byId.get(c.parentId) : undefined;
    while (cur) { if (!cur.active) return cur; cur = cur.parentId ? byId.get(cur.parentId) : undefined; }
    return null;
  };

  const columns: ColumnsType<CategoryNode> = [
    {
      title: 'Tên danh mục', key: 'name', width: 360,
      render: (_v, c) => {
        const anc = !c.active ? null : inactiveAncestor(c);
        return (
          <span className="inline-flex items-center gap-2">
            {editable && !filtering && <GripVertical size={15} className="cursor-grab text-slate-300" />}
            <Folder size={c.parentId ? 16 : 18} className={`${c.parentId ? 'text-sky-400' : 'text-[#3e91ab]'}`} />
            <span className={`font-medium ${c.active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{c.name}</span>
            {anc && <Tooltip title={`Danh mục cha “${anc.name}” đang ngừng sử dụng nên danh mục này không chọn được`}><span className="rounded bg-amber-50 px-1.5 text-[11px] text-amber-700">Cha ngừng</span></Tooltip>}
          </span>
        );
      },
    },
    { title: 'Mã danh mục', dataIndex: 'code', width: 130, render: v => <span className="font-mono text-xs text-slate-600">{v}</span> },
    { title: 'Loại tài liệu', dataIndex: 'typeId', width: 150, render: v => typeName(v) },
    {
      title: <HeaderHint label="Tài liệu" hint="Số tài liệu (gồm danh mục con), không tính thùng rác" />, key: 'docs', width: 100, align: 'right',
      render: (_v, c) => docCount[c.id]?.all || 0,
    },
    {
      title: <HeaderHint label="Hiển thị" hint="Hiện trên cây thư mục ở màn Quản lý / Cá nhân. Ẩn không ảnh hưởng tài liệu." />, key: 'visible', width: 100, align: 'center',
      render: (_v, c) => (
        <Tooltip title={c.visible ? 'Đang hiện trên cây thư mục — bấm để ẩn' : 'Đang ẩn khỏi cây thư mục — bấm để hiện'}>
          <button disabled={!editable} onClick={() => toggle(c, 'visible')} className={c.visible ? 'text-[#3e91ab]' : 'text-slate-300'}>
            {c.visible ? <Eye size={17} /> : <EyeOff size={17} />}
          </button>
        </Tooltip>
      ),
    },
    {
      title: <HeaderHint label="Trạng thái" hint="Đang sử dụng: được chọn khi tạo tài liệu mới" />, key: 'active', width: 170,
      render: (_v, c) => (
        <div className="flex items-center gap-2">
          <Switch size="small" checked={c.active} disabled={!editable} onChange={() => toggle(c, 'active')} />
          <span className="text-sm text-slate-600">{c.active ? 'Đang sử dụng' : 'Ngừng sử dụng'}</span>
        </div>
      ),
    },
    ...(editable ? [{
      title: 'Hành động', key: 'act', width: 130, align: 'center' as const,
      render: (_v: unknown, c: CategoryNode) => (
        <div className="flex items-center justify-center gap-3">
          <Tooltip title={categoryDepth(categories, c.id) >= MAX_DEPTH ? `Tối đa ${MAX_DEPTH} cấp` : 'Thêm danh mục con'}>
            <button disabled={categoryDepth(categories, c.id) >= MAX_DEPTH} onClick={() => openForm('new', c)} className="text-slate-400 hover:text-[#357f96] disabled:opacity-30"><FolderPlus size={16} /></button>
          </Tooltip>
          <Tooltip title="Sửa"><button onClick={() => openForm(c)} className="text-slate-400 hover:text-[#357f96]"><Pencil size={16} /></button></Tooltip>
          <Tooltip title="Xóa"><button onClick={() => remove(c)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button></Tooltip>
        </div>
      ),
    }] : []),
  ];

  const siblings = categories.filter(c => c.parentId === (parentId ?? null) && c.id !== editingId);

  return (
    <DocShell
      title="Danh mục tài liệu"
      subtitle={`${categories.length} danh mục · tối đa ${MAX_DEPTH} cấp`}
      actions={editable && <Button type="primary" icon={<Plus size={16} />} onClick={() => openForm('new')}>Tạo mới</Button>}
    >
      {ctx}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input allowClear prefix={<Search size={15} className="text-slate-400" />} placeholder="Tìm nhanh mã, tên danh mục…" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" />
        <Select value={typeFilter} onChange={setTypeFilter} className="w-44" options={[{ value: '', label: 'Tất cả loại' }, ...types.map(t => ({ value: t.id, label: t.name }))]} />
        <Select value={status} onChange={setStatus} className="w-44" options={[
          { value: 'all', label: 'Tất cả trạng thái' }, { value: 'active', label: 'Đang sử dụng' }, { value: 'inactive', label: 'Ngừng sử dụng' }, { value: 'hidden', label: 'Đang ẩn' },
        ]} />
        {!filtering && (
          <div className="ml-auto flex gap-1">
            <Button size="small" type="link" onClick={() => setExpanded(allIds)}>Mở rộng tất cả</Button>
            <Button size="small" type="link" onClick={() => setExpanded([])}>Thu gọn</Button>
          </div>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white">
        <Table<CategoryNode>
          rowKey="id" columns={columns} dataSource={tree} scroll={{ x: 1150 }}
          expandable={{ expandedRowKeys: expandedKeys, onExpandedRowsChange: keys => !filtering && setExpanded([...keys]), indentSize: 28 }}
          locale={{ emptyText: filtering ? 'Không có danh mục phù hợp bộ lọc' : 'Chưa có danh mục' }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50], showTotal: t => `${t} danh mục gốc` }}
          onRow={c => (editable && !filtering ? {
            draggable: true,
            onDragStart: e => { e.stopPropagation(); e.dataTransfer.effectAllowed = 'move'; setDrag({ id: c.id, parentId: c.parentId }); },
            onDragOver: e => {
              if (!drag || drag.parentId !== c.parentId || drag.id === c.id) return;
              e.preventDefault();
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const pos = e.clientY - r.top > r.height / 2 ? 'bottom' : 'top';
              if (drag.over !== c.id || drag.pos !== pos) setDrag({ ...drag, over: c.id, pos });
            },
            onDrop: e => { e.preventDefault(); drop(c); },
            onDragEnd: () => setDrag(null),
            className: [
              drag?.id === c.id ? 'opacity-50' : '',
              drag?.over === c.id ? (drag.pos === 'top' ? '[&>td]:!border-t-2 [&>td]:!border-t-[#3e91ab]' : '[&>td]:!border-b-2 [&>td]:!border-b-[#3e91ab]') : '',
              c.parentId ? 'bg-slate-50/50' : '',
            ].join(' '),
          } : { className: c.parentId ? 'bg-slate-50/50' : '' })}
        />
      </div>
      {editable && !filtering && <div className="mt-2 text-xs text-slate-400">Kéo thả để sắp xếp trong cùng một cấp. Để chuyển sang danh mục cha khác, dùng Sửa → Danh mục cha.</div>}

      <Drawer
        open={!!editing} onClose={() => setEditing(null)} width={460} destroyOnHidden
        title={editingId ? 'Cập nhật danh mục' : 'Thêm mới danh mục'}
        footer={<div className="flex justify-end gap-2"><Button onClick={() => setEditing(null)}>Hủy bỏ</Button><Button type="primary" onClick={submit}>Lưu</Button></div>}
      >
        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item name="parentId" label="Danh mục cha" extra={`Để trống nếu là danh mục gốc. Tối đa ${MAX_DEPTH} cấp.`}>
            <TreeSelect allowClear treeDefaultExpandAll treeData={parentTree} placeholder="— Danh mục gốc —" onChange={v => onParentChange(v ?? null)} showSearch treeNodeFilterProp="title" />
          </Form.Item>
          <Form.Item
            name="code" label="Mã danh mục"
            rules={[
              { required: true, whitespace: true, message: 'Nhập mã danh mục' },
              { pattern: /^[A-Za-z0-9._-]{2,30}$/, message: '2–30 ký tự: chữ không dấu, số, . _ -' },
              { validator: (_r, v) => (v && categories.some(c => c.id !== editingId && c.code.toUpperCase() === String(v).trim().toUpperCase()) ? Promise.reject(new Error('Mã đã tồn tại')) : Promise.resolve()) },
            ]}
          >
            <Input maxLength={30} placeholder="VD: DM09" />
          </Form.Item>
          <Form.Item
            name="name" label="Tên danh mục"
            rules={[
              { required: true, whitespace: true, message: 'Nhập tên danh mục' },
              { max: 150, message: 'Tối đa 150 ký tự' },
              { validator: (_r, v) => (v && siblings.some(c => c.name.trim().toLowerCase() === String(v).trim().toLowerCase()) ? Promise.reject(new Error('Đã có danh mục cùng tên trong cùng cấp')) : Promise.resolve()) },
            ]}
          >
            <Input maxLength={150} showCount placeholder="Nhập tên danh mục…" />
          </Form.Item>
          <Form.Item
            name="typeId" label="Loại tài liệu" rules={[{ required: true, message: 'Chọn loại tài liệu' }]}
            extra={parent ? 'Danh mục con dùng cùng loại với danh mục cha.' : editingId && descendantIds(categories, editingId).length ? 'Đổi loại sẽ áp dụng cho toàn bộ danh mục con.' : undefined}
          >
            <Select
              disabled={!!parent} placeholder="Chọn loại"
              options={types.map(t => ({ value: t.id, label: t.active ? t.name : `${t.name} (ngừng sử dụng)`, disabled: !t.active }))}
            />
          </Form.Item>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="active" label="Trạng thái" valuePropName="checked"><Switch checkedChildren="Đang dùng" unCheckedChildren="Ngừng" /></Form.Item>
            <Form.Item name="visible" label="Hiện trên cây thư mục" valuePropName="checked"><Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" /></Form.Item>
          </div>
          <button type="submit" hidden />
        </Form>
      </Drawer>
    </DocShell>
  );
}
